import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router';

/** How the ungrouped list draws its rows. */
export type PropertyLayout = 'list' | 'grid';

/** The query param that carries the list/grid layout. */
export const LAYOUT_PARAM = 'layout';

/**
 * Coerces a raw query value into a valid {@link PropertyLayout}, defaulting
 * to `list`.
 *
 * @param raw - The raw `?layout=` value (or null when absent).
 * @returns A valid layout.
 */
export function parseLayout(raw: string | null): PropertyLayout {
  return raw === 'grid' ? 'grid' : 'list';
}

/**
 * The list/grid layout of the property list, kept in the URL (`?layout=`) so
 * it survives reloads and shared links like the grouping does.
 *
 * The grid is a desktop presentation of the ungrouped list: on a phone the
 * cards already are the list, and a grouping draws its own list/summary. In
 * either case the effective layout is `list` whatever the URL says, so a
 * stale `?layout=grid` never asks the server for covers nobody draws.
 *
 * @param available - Whether the grid can be shown right now.
 * @returns The effective layout and its setter.
 */
export function usePropertyLayout(available: boolean) {
  const [searchParams, setSearchParams] = useSearchParams();
  const requested = useMemo(() => parseLayout(searchParams.get(LAYOUT_PARAM)), [searchParams]);
  const layout: PropertyLayout = available ? requested : 'list';

  const setLayout = useCallback(
    (next: PropertyLayout) => {
      setSearchParams((prev) => {
        const params = new URLSearchParams(prev);
        if (next === 'list') params.delete(LAYOUT_PARAM);
        else params.set(LAYOUT_PARAM, next);
        return params;
      }, { replace: true });
    },
    [setSearchParams],
  );

  return { layout, setLayout };
}
